/**
 * Gmail-style inline composer that lives at the bottom of the conversation
 * timeline (instead of a separate drawer), opened by the per-message and
 * end-of-thread Reply / Forward actions.
 *
 * Two modes:
 *   - `reply`   — To is prefilled with the thread's handle; the message being
 *                 replied to is appended as a collapsed "…" quote block, the
 *                 way Gmail folds the quoted original into the draft.
 *   - `forward` — To starts empty and focused; the source message is embedded
 *                 as a "---------- Forwarded message ----------" block with its
 *                 From / Date / Subject / To header, and its attachments are
 *                 carried over by reference.
 *
 * Sending posts to the same POST /conversations/:id/messages endpoint the
 * Reply drawer uses, so approval routing, provider fan-out and optimistic
 * message insertion all behave identically.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { Check, ChevronDown, ChevronUp, Eye, FileText, Lock, MessageSquareText, MoreHorizontal, Paperclip, Send, ShieldAlert, ShieldCheck, ShieldX, Sparkles, Trash2, X } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useUploadAttachment } from '../../../hooks/useUploadAttachment.js'
import RichTextEditor, { isEmptyHtml } from '../../common/RichTextEditor.js'
import AiComposeBar from './ai/AiComposeBar.js'
import ReplyPreviewDialog from './ReplyPreviewDialog.js'
import { useReplySubject } from './useReplySubject.js'
import EmailChipsInput, { hasInvalidRecipient } from '../../common/EmailChipsInput.js'
import type { ConversationDetailResponse, Message } from '../../../modules/conversations/types.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { cn } from '../../../utils/cn.js'
import { R, htmlToText, inputClass } from './primitives.js'
import { SafeHtml } from '../../common/SafeHtml.js'

type Conversation = ConversationDetailResponse['conversation']

export type ComposerMode = 'reply' | 'forward'

/** Channels where an agent may direct a message at an arbitrary recipient. */
const FORWARDABLE_CHANNELS = new Set(['email', 'sms', 'whatsapp'])

// Multilingual patterns for greetings (start of message)
const GREETING_PATTERN = /^(hi|hello|hey|dear|greetings|bonjour|salut|cher|chère|hola|estimado|estimada|querido|querida|hallo|liebe|lieber|sehr geehrte|ciao|gentile|buongiorno|olá|prezado|prezada|привет|здравствуйте|уважаемый|مرحبا|عزيزي|नमस्ते|प्रिय|你好|亲爱的|こんにちは|안녕하세요)/i

// Multilingual patterns for sign-offs (end of message)
const SIGNOFF_PATTERN = /(regards|sincerely|best|thanks|cheers|respectfully|cordialement|merci|bien à vous|salutations|saludos|atentamente|gracias|mit freundlichen grüßen|danke|beste grüße|cordiali saluti|grazie|distinti saluti|atenciosamente|obrigado|obrigada|с уважением|спасибо|شكرا|مع تحياتي|धन्यवाद|सादर|谢谢|此致敬礼|ありがとう|敬具|감사합니다)/i

// Multilingual patterns for empathy phrases
const EMPATHY_PATTERN = /\b(sorry|apolog|understand|unfortunate|frustrat|inconvenience|appreciate|we value|thank you|please let me know|happy to help|here to help|we hear you|we regret|désolé|excus|comprend|malheureus|désagrément|apprécions|nous sommes là|lo siento|disculp|comprend|lament|inconvenient|agradec|estamos aquí|entschuldig|versteh|bedauer|unannehmlich|wir schätzen|ci scusiamo|comprendiamo|spiacent|siamo qui|desculp|compreend|lament|inconveniência|agradec|estamos aqui|извин|понима|сожале|неудобств|ценим|نعتذر|نفهم|نأسف|क्षमा|समझ|असुविध|抱歉|理解|遗憾|不便|申し訳|ご理解|죄송|이해|불편)\b/gi

// Multilingual patterns for formal tone
const FORMAL_PATTERN = /\b(regards|sincerely|furthermore|however|therefore|pursuant|kindly|aforementioned|cordialement|veuillez|conformément|toutefois|atentamente|por favor|conforme|sin embargo|mit freundlichen grüßen|bitte|gemäß|jedoch|cordiali saluti|la prego|conformemente|tuttavia|atenciosamente|por favor|conforme|contudo|с уважением|пожалуйста|в соответствии|однако)\b/gi

// Multilingual patterns for friendly tone
const FRIENDLY_PATTERN = /\b(hey|hi there|happy to|glad to|love to|awesome|great to hear|sounds good|cheers|salut|super|génial|avec plaisir|hola|genial|estupendo|con gusto|hey|toll|super|gerne|ciao|fantastico|ottimo|con piacere|oi|legal|ótimo|com prazer|привет|отлично|супер|с удовольствием|مرحبا|رائع|ممتاز|बढ़िया|शानदार|太好了|很高兴|すごい|喜んで|좋아요|기꺼이)\b/gi

// Multilingual placeholder pattern
const PLACEHOLDER_PATTERN = /\[(?:customer|name|insert|your|client|nom|nombre|kunde|cliente|имя|اسم|नाम|名前|이름)[^\]]*\]/i

/** True when Forward makes sense for this channel (a free-form To is honoured). */
export function canForwardChannel(channelOrigin: string): boolean {
  return FORWARDABLE_CHANNELS.has(channelOrigin)
}

/** Display name for whoever sent `message`, from the viewer's perspective. */
function senderNameOf(message: Message, conversation: Conversation): string {
  if (message.direction === 'outbound') return message.senderAgentName || 'Agent'
  return message.senderName || conversation.displayName || message.senderHandle || 'Customer'
}

/** Escape a string for safe interpolation into the quote header HTML. */
function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Body HTML of `message`, normalised for embedding in a quote: plain-text
 * bodies get their line breaks preserved, HTML bodies keep their markup.
 */
function bodyHtmlOf(message: Message): string {
  const body = message.body || ''
  const isPlainText = !/<[a-z][\s\S]*>/i.test(body)
  return isPlainText ? esc(body).replace(/\n/g, '<br />') : body
}

/** Gmail's "On <date>, <sender> wrote:" quote block appended to a reply. */
function buildReplyQuote(message: Message, conversation: Conversation): string {
  const who = esc(senderNameOf(message, conversation))
  const handle = message.senderHandle ? ` &lt;${esc(message.senderHandle)}&gt;` : ''
  return (
    `<div><br /></div><div>On ${esc(formatDateTime(message.createdAt))}, ${who}${handle} wrote:</div>` +
    `<blockquote style="margin:0 0 0 0.8ex;border-left:1px solid #ccc;padding-left:1ex">${bodyHtmlOf(message)}</blockquote>`
  )
}

/** Gmail's "---------- Forwarded message ----------" block. */
function buildForwardQuote(message: Message, conversation: Conversation): string {
  const who = esc(senderNameOf(message, conversation))
  const handle = message.senderHandle ? ` &lt;${esc(message.senderHandle)}&gt;` : ''
  const to = message.direction === 'outbound' ? esc(conversation.primaryHandle || 'Customer') : 'us'
  const subject = conversation.subject ? esc(conversation.subject) : `#${esc(conversation.referenceId)}`
  return (
    '<div><br /></div><div>---------- Forwarded message ----------</div>' +
    `<div>From: ${who}${handle}</div>` +
    `<div>Date: ${esc(formatDateTime(message.createdAt))}</div>` +
    `<div>Subject: ${subject}</div>` +
    `<div>To: ${to}</div><div><br /></div>` +
    bodyHtmlOf(message)
  )
}

interface PendingAttachment {
  localId: string
  name: string
  state: 'uploading' | 'uploaded' | 'error'
  attachmentId?: string
  mimeType?: string
}

interface SendResponse {
  messageId: string
  /**
   * Status the server stored. 'sent' for an internal note (nothing delivers
   * one), 'queued' for a customer message awaiting dispatch.
   */
  status?: 'pending_approval' | 'queued' | 'sent'
}

export interface InlineComposerProps {
  conversation: Conversation
  currentAgentId: string | null
  mode: ComposerMode
  /** Message the action was fired from — quoted / forwarded. Null = blank reply. */
  sourceMessage: Message | null
  /**
   * Pre-fill the editor. Set when the agent accepts the thread-level AI
   * suggested reply, so the composer opens with that draft rather than blank.
   */
  initialMessage?: string
  onCancel: () => void
  /** Fired after a successful send (the composer closes itself). */
  onSent: () => void
  appendMessage: (m: Message) => void
  replaceMessage: (oldId: string, m: Message) => void
  markMessageFailed: (id: string, error: string) => void
}

export default function InlineComposer(props: InlineComposerProps): JSX.Element {
  const { conversation, mode, sourceMessage } = props
  const isForward = mode === 'forward'
  const isFacebook = conversation.channelOrigin === 'facebook'
  const isYouTube = conversation.channelOrigin === 'youtube'
  const isTelegram = conversation.channelOrigin === 'telegram'
  /** A web (agent-portal) ticket with an email address can reply by web or by email. */
  const isWebTicket = conversation.channelOrigin === 'web'
  const webEmail = isWebTicket ? (conversation.clientEmail ?? null) : null
  // Starts on email when the ticket has an address — before the choice
  // existed every such reply was mailed to the client.
  const [replyVia, setReplyVia] = useState<'web' | 'email'>(webEmail ? 'email' : 'web')
  /** Email addresses several people plus Cc/Bcc — email tickets, and a web ticket replying by email. */
  const isEmail = conversation.channelOrigin === 'email' || (isWebTicket && !!webEmail && replyVia === 'email')
  /** Channels whose recipient is fixed by the thread — no editable To field. */
  const showTo = !isYouTube && !isFacebook && !isTelegram

  const rootRef = useRef<HTMLDivElement>(null)
  const toRef = useRef<HTMLInputElement>(null)

  const quote = useMemo(() => {
    if (!sourceMessage) return ''
    // Gmail-style quoting only belongs on email. On a chat channel the customer
    // sees the whole "On <date>, X wrote:" chain as literal text in their
    // WhatsApp/Messenger bubble, which is both noisy and confusing.
    // Email tickets only: on a web ticket the body is also what the client
    // portal shows, so a quoted chain would be shown there verbatim.
    if (conversation.channelOrigin !== 'email') return ''
    return isForward
      ? buildForwardQuote(sourceMessage, conversation)
      : buildReplyQuote(sourceMessage, conversation)
  }, [sourceMessage, conversation, isForward])

  const [to, setTo] = useState(isForward ? '' : (conversation.primaryHandle ?? ''))
  // Email keeps its recipients as chip lists — a reply may go to several
  // people, with Cc/Bcc alongside. Forward starts with an empty To.
  const [toList, setToList] = useState<string[]>(
    isForward ? [] : webEmail ? [webEmail] : !conversation.primaryHandle || isWebTicket ? [] : [conversation.primaryHandle],
  )
  // A web ticket's email reply keeps the Cc/Bcc the ticket was created with.
  const [ccList, setCcList] = useState<string[]>(isWebTicket && !isForward ? (conversation.creationCc ?? []) : [])
  const [bccList, setBccList] = useState<string[]>(isWebTicket && !isForward ? (conversation.creationBcc ?? []) : [])
  const [showCc, setShowCc] = useState(ccList.length > 0)
  const [showBcc, setShowBcc] = useState(bccList.length > 0)
  const replySubject = useReplySubject(conversation.id, isEmail)
  // Seeded when the thread-level suggested reply is clicked, so the composer
  // opens with the draft already in it.
  const [message, setMessage] = useState(props.initialMessage ?? '')
  const [showQuote, setShowQuote] = useState(false)
  const [isPrivate, setIsPrivate] = useState(false)
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [attachmentIds, setAttachmentIds] = useState<string[]>(
    // A forward carries the original's attachments along by reference.
    isForward ? (sourceMessage?.attachments ?? []).map((a) => a.id) : [],
  )
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const { upload } = useUploadAttachment()

  // Content quality metrics — computed from the message text
  const [showQualityDetails, setShowQualityDetails] = useState(false)

  const contentQuality = useMemo(() => {
    const text = htmlToText(message).trim()
    if (text.length < 10) return null

    // Confidence score — based on text quality heuristics (multilingual)
    let confidence = 70
    if (text.length > 100) confidence += 10
    if (text.length > 250) confidence += 5
    if (GREETING_PATTERN.test(text)) confidence += 5
    if (SIGNOFF_PATTERN.test(text)) confidence += 5
    if (!PLACEHOLDER_PATTERN.test(text)) confidence += 5
    if (text.length < 50) confidence -= 15
    const words = text.split(/\s+/)
    const unique = new Set(words.map((w) => w.toLowerCase()))
    if (words.length > 10 && unique.size / words.length < 0.6) confidence -= 10
    confidence = Math.max(0, Math.min(100, confidence))

    // Empathy score (multilingual)
    const empathyMatches = text.match(EMPATHY_PATTERN)
    const empathyCount = empathyMatches ? empathyMatches.length : 0
    const empathy = empathyCount === 0 ? 30 : empathyCount === 1 ? 65 : empathyCount === 2 ? 85 : 95

    // Clarity score (supports multiple sentence terminators for different languages)
    const sentences = text.split(/[.!?。！？]+/).filter((s) => s.trim().length > 0)
    let clarity = 100
    if (sentences.length > 0) {
      const avgWordsPerSentence = words.length / sentences.length
      const longWords = words.filter((w) => w.length > 8).length
      const longWordRatio = words.length > 0 ? longWords / words.length : 0
      if (avgWordsPerSentence > 25) clarity -= 20
      else if (avgWordsPerSentence > 20) clarity -= 10
      if (longWordRatio > 0.25) clarity -= 15
      else if (longWordRatio > 0.15) clarity -= 5
      if (sentences.length < 2) clarity -= 10
    }
    clarity = Math.max(0, Math.min(100, clarity))

    // Human touch indicators (multilingual)
    const hasGreeting = GREETING_PATTERN.test(text)
    const hasSignOff = SIGNOFF_PATTERN.test(text)
    const hasPersonalization = text.length > 80 && !PLACEHOLDER_PATTERN.test(text)

    // Tone detection (multilingual)
    const formalCount = (text.match(FORMAL_PATTERN) || []).length
    const friendlyCount = (text.match(FRIENDLY_PATTERN) || []).length
    let tone: 'Friendly' | 'Formal' | 'Concise' | 'Neutral' = 'Neutral'
    if (friendlyCount > formalCount) tone = 'Friendly'
    else if (formalCount > 1) tone = 'Formal'
    else if (text.length < 150) tone = 'Concise'

    // Overall trust score
    const weighted = Math.round(confidence * 0.4 + clarity * 0.35 + empathy * 0.25)
    let bonus = 0
    if (hasGreeting) bonus += 3
    if (hasSignOff) bonus += 3
    if (hasPersonalization) bonus += 4
    const score = Math.min(100, weighted + bonus)

    const level: 'high' | 'medium' | 'low' = score >= 80 ? 'high' : score >= 60 ? 'medium' : 'low'
    const label = level === 'high' ? 'Ready to Send' : level === 'medium' ? 'Review Recommended' : 'Needs Improvement'

    return { score, level, label, confidence, clarity, empathy, hasGreeting, hasSignOff, hasPersonalization, tone }
  }, [message])

  // Scroll the composer into view and focus the field the agent must fill first.
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    if (isForward) toRef.current?.focus()
  }, [isForward])

  const carriedAttachments = isForward ? (sourceMessage?.attachments ?? []) : []
  const uploading = pending.some((p) => p.state === 'uploading')
  const plainLen = htmlToText(message).length
  const isPublic = !isPrivate
  const needsRecipient = isForward && showTo && isPublic
  // Email: at least one To chip, and nothing malformed on any of the lists.
  const emailRecipientsInvalid =
    isEmail &&
    isPublic &&
    (toList.length === 0 ||
      hasInvalidRecipient(toList) ||
      hasInvalidRecipient(ccList) ||
      hasInvalidRecipient(bccList))
  const canSend =
    !sending &&
    !uploading &&
    !emailRecipientsInvalid &&
    (!isEmptyHtml(message) || attachmentIds.length > 0) &&
    (!isPublic || plainLen >= 10) &&
    (isEmail || !needsRecipient || to.trim().length > 0)

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      const list = Array.from(files)
      const additions: PendingAttachment[] = list.map((f) => ({
        localId: crypto.randomUUID(),
        name: f.name,
        state: 'uploading',
        mimeType: f.type || 'application/octet-stream',
      }))
      setPending((prev) => [...prev, ...additions])
      additions.forEach((a, i) => {
        const file = list[i]
        if (!file) return
        void (async () => {
          try {
            const res = await upload(file)
            setPending((prev) =>
              prev.map((p) =>
                p.localId === a.localId
                  ? { ...p, state: 'uploaded', attachmentId: res.attachmentId, mimeType: res.mimeType }
                  : p,
              ),
            )
            setAttachmentIds((prev) => [...prev, res.attachmentId])
          } catch {
            setPending((prev) => prev.map((p) => (p.localId === a.localId ? { ...p, state: 'error' } : p)))
          }
        })()
      })
    },
    [upload],
  )

  const removeAttachment = (localId: string): void => {
    setPending((prev) => {
      const removed = prev.find((p) => p.localId === localId)
      if (removed?.attachmentId) setAttachmentIds((ids) => ids.filter((id) => id !== removed.attachmentId))
      return prev.filter((p) => p.localId !== localId)
    })
  }

  async function submit(): Promise<void> {
    if (!canSend) return
    setError(null)
    // The quoted original is appended at send time so it never clutters the
    // editor while the agent types — exactly how Gmail folds it behind "…".
    const html = `${message}${quote}`
    const text = htmlToText(html)
    const visibility = isPublic ? 'customer' : 'internal'
    const tempId = `optimistic-${crypto.randomUUID()}`
    const optimistic: Message = {
      id: tempId,
      conversationId: conversation.id,
      direction: 'outbound',
      senderHandle: null,
      senderAgentId: props.currentAgentId,
      senderAgentName: null,
      senderName: null,
      body: text,
      visibility,
      ...(isEmail && isPublic
        ? {
          toRecipients: [...toList],
          ...(ccList.length ? { ccRecipients: [...ccList] } : {}),
          ...(bccList.length ? { bccRecipients: [...bccList] } : {}),
          ...(replySubject.customSubject ? { emailSubject: replySubject.customSubject } : {}),
        }
        : {}),
      attachments: attachmentIds.map((id) => ({
        id,
        mimeType: 'application/octet-stream',
        filename: 'attachment',
        sizeBytes: 0,
      })),
      status: 'sending',
      createdAt: new Date().toISOString(),
    }
    props.appendMessage(optimistic)
    setSending(true)
    try {
      const resp = await api.post<SendResponse>(`/conversations/${conversation.id}/messages`, {
        contentText: text,
        contentHtml: html,
        visibility,
        ...(isEmail && isPublic
          ? {
            ...(toList.length ? { recipientHandles: toList } : {}),
            ...(ccList.length ? { cc: ccList } : {}),
            ...(bccList.length ? { bcc: bccList } : {}),
            ...(replySubject.customSubject ? { emailSubject: replySubject.customSubject } : {}),
          }
          : showTo && isPublic && to.trim()
            ? { recipientHandle: to.trim() }
            : {}),
        ...(isWebTicket && isPublic ? { replyVia: isEmail ? 'email' : 'web' } : {}),
        ...(attachmentIds.length ? { attachments: attachmentIds } : {}),
      })
      props.replaceMessage(tempId, {
        ...optimistic,
        id: resp.messageId,
        // Use what the server actually stored. An internal note is already
        // 'sent' — assuming 'queued' left it showing "Sending" forever, since
        // nothing delivers a note and no status event ever follows.
        status: resp.status ?? 'queued',
      })
      props.onSent()
    } catch (err) {
      props.markMessageFailed(tempId, err instanceof Error ? err.message : 'Send failed')
      setError(err instanceof Error ? err.message : 'Send failed')
    } finally {
      setSending(false)
    }
  }

  return (
    <div ref={rootRef} className={`mt-3 ${R} border border-slate-200 bg-white shadow-sm`}>
      {/* Header — mode + close, mirroring Gmail's inline draft chrome. */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {isPrivate ? 'Internal note' : isForward ? 'Forward' : 'Reply'}
        </span>
        <div className="flex items-center gap-1">
          {webEmail && !isPrivate && (
            <div role="group" aria-label="Reply via" className={`mr-1 inline-flex ${R} border border-slate-200 p-0.5`}>
              {(['web', 'email'] as const).map((via) => (
                <button
                  key={via}
                  type="button"
                  aria-pressed={replyVia === via}
                  onClick={() => setReplyVia(via)}
                  title={via === 'web' ? 'Reply on the ticket / client portal only' : `Also email the reply to ${webEmail}`}
                  className={cn(
                    `inline-flex items-center gap-1 ${R} px-2 py-0.5 text-[11px] font-semibold transition-colors`,
                    replyVia === via ? 'bg-[#3366FF] text-white' : 'text-slate-500 hover:bg-slate-100',
                  )}
                >
                  {via === 'web' ? 'Web' : 'Email'}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsPrivate((v) => !v)}
            title={isPrivate ? 'Switch back to a customer-visible reply' : 'Keep this as an internal note'}
            className={cn(
              `inline-flex items-center gap-1 ${R} px-2 py-1 text-[11px] font-semibold transition-colors`,
              isPrivate ? 'bg-amber-500 text-white' : 'text-slate-500 hover:bg-slate-100',
            )}
          >
            <Lock className="h-3 w-3" />
            Private
          </button>
          <button
            type="button"
            onClick={props.onCancel}
            title="Discard"
            className={`${R} p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="px-4 py-3">
        {/* Email addresses a chip list (with Cc/Bcc); other channels keep the
            single free-text recipient. */}
        {isEmail && isPublic && (
          <>
            <div className="mb-1 flex items-start gap-2 border-b border-slate-100 pb-1.5">
              <span className="mt-2 w-8 shrink-0 text-xs font-semibold text-slate-500">To</span>
              <div className="min-w-0 flex-1">
                <EmailChipsInput
                  value={toList}
                  onChange={setToList}
                  variant="inline"
                  autoFocus={isForward}
                  ariaLabel="To recipients"
                  placeholder={isForward ? 'Recipient address' : 'Add recipients'}
                />
              </div>
              <div className="mt-2 flex shrink-0 items-center gap-2">
                {!showCc && (
                  <button
                    type="button"
                    onClick={() => setShowCc(true)}
                    className="text-xs font-semibold text-slate-500 hover:text-[#3366FF]"
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    type="button"
                    onClick={() => setShowBcc(true)}
                    className="text-xs font-semibold text-slate-500 hover:text-[#3366FF]"
                  >
                    Bcc
                  </button>
                )}
              </div>
            </div>

            {showCc && (
              <div className="mb-1 flex items-start gap-2 border-b border-slate-100 pb-1.5">
                <span className="mt-2 w-8 shrink-0 text-xs font-semibold text-slate-500">Cc</span>
                <div className="min-w-0 flex-1">
                  <EmailChipsInput
                    value={ccList}
                    onChange={setCcList}
                    variant="inline"
                    autoFocus
                    ariaLabel="Cc recipients"
                    placeholder="Visible to everyone on the thread"
                  />
                </div>
              </div>
            )}

            {showBcc && (
              <div className="mb-2 flex items-start gap-2 border-b border-slate-100 pb-1.5">
                <span className="mt-2 w-8 shrink-0 text-xs font-semibold text-slate-500">Bcc</span>
                <div className="min-w-0 flex-1">
                  <EmailChipsInput
                    value={bccList}
                    onChange={setBccList}
                    variant="inline"
                    autoFocus
                    ariaLabel="Bcc recipients"
                    placeholder="Hidden from all other recipients"
                  />
                </div>
              </div>
            )}

            <div className="mb-2 flex items-center gap-2 border-b border-slate-100 pb-1.5">
              <span className="w-8 shrink-0 text-xs font-semibold text-slate-500">Subj</span>
              <input
                value={replySubject.subject}
                onChange={(e) => replySubject.setSubject(e.target.value)}
                maxLength={998}
                aria-label="Subject"
                placeholder={replySubject.defaultSubject || 'Subject'}
                title="Leave as is to use the default subject. The ticket reference is added automatically."
                className={`${inputClass} border-transparent bg-transparent px-0 py-1`}
              />
            </div>
          </>
        )}

        {showTo && !isEmail && isPublic && (
          <div className="mb-2 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="w-8 shrink-0 text-xs font-semibold text-slate-500">To</span>
            <input
              ref={toRef}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={isForward ? 'Recipient address or number' : 'Recipient'}
              className={`${inputClass} border-transparent bg-transparent px-0 py-1`}
            />
          </div>
        )}

        <RichTextEditor
          value={message}
          onChange={setMessage}
          height={160}
          placeholder={isForward ? 'Add a message (optional)…' : 'Write your reply…'}
        />

        <AiComposeBar value={message} onChange={setMessage} html />

        {/* Collapsed quote — the "…" pill Gmail shows above the quoted original. */}
        {quote && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowQuote((v) => !v)}
              title={showQuote ? 'Hide quoted text' : 'Show quoted text'}
              aria-expanded={showQuote}
              className={`inline-flex items-center ${R} bg-slate-100 px-2 py-0.5 text-slate-500 transition-colors hover:bg-slate-200`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {showQuote && (
              <SafeHtml
                className="kc3-quote-preview mt-2 border-l-2 border-slate-200 pl-3 text-xs leading-relaxed text-slate-500"
                html={quote}
              />
            )}
          </div>
        )}

        {/* Attachments — forwarded originals first, then anything newly added. */}
        {(carriedAttachments.length > 0 || pending.length > 0) && (
          <ul className="mt-3 space-y-1.5">
            {carriedAttachments.map((a) => (
              <li
                key={a.id}
                className={`flex items-center gap-2 ${R} bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600`}
              >
                <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="truncate">{a.filename}</span>
                <span className="text-slate-400">· forwarded</span>
              </li>
            ))}
            {pending.map((p) => (
              <li
                key={p.localId}
                className={`flex items-center justify-between ${R} bg-slate-100 px-2.5 py-1.5 text-xs text-slate-700`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="truncate">{p.name}</span>
                  {p.state === 'uploading' && (
                    <span className="h-2.5 w-2.5 animate-spin rounded-full border border-slate-300 border-t-slate-600" />
                  )}
                  {p.state === 'error' && <span className="text-red-500">failed</span>}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(p.localId)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="mt-2 text-[11px] text-red-500">{error}</p>}
        {isEmail && isPublic && toList.length === 0 && (
          <p className="mt-2 text-[11px] text-slate-400">Add at least one recipient.</p>
        )}
        {isEmail &&
          isPublic &&
          (hasInvalidRecipient(toList) || hasInvalidRecipient(ccList) || hasInvalidRecipient(bccList)) && (
          <p className="mt-2 text-[11px] text-red-500">
            One of the addresses isn&apos;t valid — remove or fix it before sending.
          </p>
        )}
        {isPublic && plainLen > 0 && plainLen < 10 && (
          <p className="mt-2 text-[11px] text-slate-400">A customer-visible reply needs at least 10 characters.</p>
        )}

        {/* Content Quality Indicator — shows real-time quality metrics */}
        {isPublic && contentQuality && (
          <div className="mt-3">
            {/* Compact quality badge — click to expand */}
            <button
              type="button"
              onClick={() => setShowQualityDetails((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all hover:shadow-sm ${
                contentQuality.level === 'high'
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100'
                  : contentQuality.level === 'medium'
                  ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200/60 hover:bg-amber-100'
                  : 'bg-red-50 text-red-700 ring-1 ring-red-200/60 hover:bg-red-100'
              }`}
              title="Click to see content quality details"
            >
              {contentQuality.level === 'high' ? (
                <ShieldCheck className="h-3.5 w-3.5" />
              ) : contentQuality.level === 'medium' ? (
                <ShieldAlert className="h-3.5 w-3.5" />
              ) : (
                <ShieldX className="h-3.5 w-3.5" />
              )}
              <span className="font-bold">{contentQuality.score}</span>
              <span>• {contentQuality.label}</span>
              {showQualityDetails ? (
                <ChevronUp className="h-3 w-3 opacity-60" />
              ) : (
                <ChevronDown className="h-3 w-3 opacity-60" />
              )}
            </button>

            {/* Expanded quality details */}
            {showQualityDetails && (
              <div className={`mt-2 overflow-hidden rounded-[4px] border ${
                contentQuality.level === 'high'
                  ? 'border-emerald-200 bg-emerald-50/50'
                  : contentQuality.level === 'medium'
                  ? 'border-amber-200 bg-amber-50/50'
                  : 'border-red-200 bg-red-50/50'
              }`}>
                {/* Score breakdown */}
                <div className="grid grid-cols-3 gap-3 px-3 py-2.5">
                  {/* Content Quality */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-slate-500">Content</span>
                      <span className={`text-[10px] font-bold ${
                        contentQuality.confidence >= 80 ? 'text-emerald-600' :
                        contentQuality.confidence >= 60 ? 'text-amber-600' : 'text-red-600'
                      }`}>{contentQuality.confidence}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={`h-full rounded-full transition-all ${
                          contentQuality.confidence >= 80 ? 'bg-emerald-500' :
                          contentQuality.confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${contentQuality.confidence}%` }}
                      />
                    </div>
                  </div>

                  {/* Clarity */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-slate-500">Clarity</span>
                      <span className={`text-[10px] font-bold ${
                        contentQuality.clarity >= 80 ? 'text-emerald-600' :
                        contentQuality.clarity >= 60 ? 'text-amber-600' : 'text-red-600'
                      }`}>{contentQuality.clarity}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={`h-full rounded-full transition-all ${
                          contentQuality.clarity >= 80 ? 'bg-emerald-500' :
                          contentQuality.clarity >= 60 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${contentQuality.clarity}%` }}
                      />
                    </div>
                  </div>

                  {/* Empathy */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-slate-500">Empathy</span>
                      <span className={`text-[10px] font-bold ${
                        contentQuality.empathy >= 80 ? 'text-emerald-600' :
                        contentQuality.empathy >= 60 ? 'text-amber-600' : 'text-red-600'
                      }`}>{contentQuality.empathy}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={`h-full rounded-full transition-all ${
                          contentQuality.empathy >= 80 ? 'bg-emerald-500' :
                          contentQuality.empathy >= 60 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${contentQuality.empathy}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Detected markers */}
                <div className="flex flex-wrap items-center gap-2 border-t border-inherit px-3 py-2">
                  <span className="text-[10px] font-medium text-slate-500">Detected:</span>
                  {contentQuality.hasGreeting && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                      <MessageSquareText className="h-2.5 w-2.5" /> Greeting
                    </span>
                  )}
                  {contentQuality.hasSignOff && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                      <Check className="h-2.5 w-2.5" /> Sign-off
                    </span>
                  )}
                  {contentQuality.hasPersonalization && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                      <Sparkles className="h-2.5 w-2.5" /> Personalized
                    </span>
                  )}
                  <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                    {contentQuality.tone} tone
                  </span>
                  {!contentQuality.hasGreeting && !contentQuality.hasSignOff && !contentQuality.hasPersonalization && (
                    <span className="text-[10px] italic text-slate-400">Add greeting/sign-off for better quality</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer — pill Send on the left like Gmail, attach + discard alongside. */}
      <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-4 py-2.5">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSend}
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-5 py-1.5 text-[13px] font-medium text-white transition-colors',
            isPrivate ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#3366FF] hover:bg-[#2952CC]',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <Send className="h-4 w-4" />
          {sending ? 'Sending…' : isPrivate ? 'Add note' : 'Send'}
        </button>
        {/* Preview — public replies only; an internal note is never templated
            or delivered, so there is nothing to preview. Renders exactly what
            submit() posts as contentHtml, quoted original included. */}
        {isPublic && (
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={isEmptyHtml(message)}
            title={isEmptyHtml(message) ? 'Write a reply to preview it' : 'See how this reply reaches the customer'}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-[13px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
        )}
        <label
          title="Attach files"
          className="inline-flex cursor-pointer items-center rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <Paperclip className="h-4 w-4" />
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </label>
        <div className="flex-1" />
        <button
          type="button"
          onClick={props.onCancel}
          title="Discard draft"
          className="inline-flex items-center rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <ReplyPreviewDialog
        open={previewOpen}
        conversationId={conversation.id}
        contentHtml={`${message}${quote}`}
        emailSubject={isEmail && isPublic ? replySubject.customSubject : undefined}
        onClose={() => setPreviewOpen(false)}
      />

      {/* Quoted-preview styling — kept narrow so email markup can't leak out. */}
      <style>{`
        .kc3-quote-preview blockquote { margin: 0.25em 0 0.25em 0.8ex; border-left: 1px solid #d1d5db; padding-left: 1ex; }
        .kc3-quote-preview img { max-width: 100%; height: auto; }
        .kc3-quote-preview a { color: #3366FF; }
        .kc3-quote-preview table { max-width: 100%; }
      `}</style>
    </div>
  )
}
